import React, { useEffect, useState } from 'react';

type TagTone = 'info' | 'warning' | 'tip' | 'accent';

type Tag = {
  label: string;
  tone: TagTone;
};

type QuickRefItem = {
  label: string;
  value: string;
};

type PageSection = {
  id: string;
  title: string;
  content: React.ReactNode;
};

type Page = {
  id: string;
  title: string;
  group: string;
  summary: string;
  version?: string;
  tags?: Tag[];
  quickRef?: QuickRefItem[];
  sections: PageSection[];
};

type NavGroup = {
  id: string;
  label: React.ReactNode;
  pages: string[];
};

type ConfigRow = {
  key: string;
  value: string;
  description: string;
};

type ConfigSection = {
  id: string;
  title: string;
  rows: ConfigRow[];
};

const ConfigReference: React.FC<{
  sections: ConfigSection[];
  filter: string;
  onFilterChange: (value: string) => void;
  inputId: string;
}> = ({ sections, filter, onFilterChange, inputId }) => {
  const normalized = filter.trim().toLowerCase();
  const filteredSections = sections
    .map((section) => {
      const rows = section.rows.filter((row) => {
        if (!normalized) return true;
        return row.key.toLowerCase().includes(normalized) || row.description.toLowerCase().includes(normalized);
      });
      return { ...section, rows };
    })
    .filter((section) => section.rows.length > 0);

  return (
    <div className="config-reference">
      <div className="config-search">
        <label htmlFor={inputId}>Filter keys</label>
        <input
          id={inputId}
          type="search"
          value={filter}
          onChange={(event) => onFilterChange(event.target.value)}
          placeholder="Search by key or description"
        />
        <span className="config-hint">Tip: use Ctrl+F for page search.</span>
      </div>
      {filteredSections.length === 0 ? (
        <div className="callout callout-warning">
          <div className="callout-title">No matches</div>
          <div className="callout-body">Try another term or clear the filter.</div>
        </div>
      ) : (
        filteredSections.map((section) => (
          <div key={section.id} className="config-section">
            <h3>{section.title}</h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Default</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <code>{row.key}</code>
                      </td>
                      <td>{row.value}</td>
                      <td>{row.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const CozyModsWiki: React.FC = () => {
  const catsModVersion = '1.3.0';
  const catsSupportedVersions = [
    '1.21.11',
    '1.21.10',
    '1.21.9',
    '1.21.8',
    '1.21.7',
    '1.21.6',
    '1.21.5',
    '1.21.1',
    '1.20.1',
    '1.19.2',
  ];
  const wolvesModVersion = '1.4.0';
  const wolvesSupportedVersions = [
    '1.21.11',
    '1.21.10',
    '1.21.9',
    '1.21.8',
    '1.21.7',
    '1.21.6',
    '1.21.5',
    '1.21.1',
    '1.20.1',
    '1.19.2',
  ];
  const [currentPageId, setCurrentPageId] = useState('home');
  const [navOpen, setNavOpen] = useState(false);
  const [navSearch, setNavSearch] = useState('');
  const [configFilter, setConfigFilter] = useState('');
  const [wolvesConfigFilter, setWolvesConfigFilter] = useState('');
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPageId]);

  useEffect(() => {
    const storedTheme = localStorage.getItem('cozyTheme');
    if (storedTheme) {
      setDarkMode(storedTheme === 'dark');
      return;
    }
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setDarkMode(prefersDark);
  }, []);

  useEffect(() => {
    const theme = darkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('cozyTheme', theme);
  }, [darkMode]);

  const handleSelectPage = (pageId: string) => {
    setCurrentPageId(pageId);
    setNavOpen(false);
  };

  const PageLink: React.FC<{ pageId: string; children: React.ReactNode }> = ({ pageId, children }) => (
    <button type="button" className="inline-link" onClick={() => handleSelectPage(pageId)}>
      {children}
    </button>
  );

  const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );

  const Callout: React.FC<{ tone: TagTone; title: string; children: React.ReactNode }> = ({
    tone,
    title,
    children,
  }) => (
    <div className={`callout callout-${tone}`}>
      <div className="callout-title">{title}</div>
      <div className="callout-body">{children}</div>
    </div>
  );

  const itemIconPath = (name: string) => `/assets/items/${name}.png`;

  const ItemCard: React.FC<{ icon: string; title: string; lines: string[] }> = ({ icon, title, lines }) => (
    <div className="card item-card">
      <div className="item-card-header">
        <img src={itemIconPath(icon)} alt={`${title} icon`} className="item-icon" loading="lazy" />
        <h3>{title}</h3>
      </div>
      <ul>
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );

  const VersionChips: React.FC<{ versions: string[] }> = ({ versions }) => (
    <div className="version-grid">
      {versions.map((version) => (
        <span key={version} className="version-chip">
          {version}
        </span>
      ))}
    </div>
  );

  const catsConfigSections: ConfigSection[] = [
    {
      id: 'trading',
      title: 'Trading',
      rows: [
        { key: 'trading.ancientCodEmeraldCost', value: '3', description: 'Emerald cost for Ancient Cod trade.' },
        { key: 'trading.ancientCodTradeReceive', value: '1', description: 'Ancient Cod received per trade.' },
        { key: 'trading.ancientSalmonEmeraldCost', value: '3', description: 'Emerald cost for Ancient Salmon trade.' },
        { key: 'trading.ancientSalmonTradeReceive', value: '1', description: 'Ancient Salmon received per trade.' },
        { key: 'trading.fishermanLevelForTrade', value: '3', description: 'Fisherman level required for trades.' },
        { key: 'trading.maxTradeUsesAncientCod', value: '10', description: 'Max uses for Ancient Cod trades.' },
        { key: 'trading.maxTradeUsesAncientSalmon', value: '10', description: 'Max uses for Ancient Salmon trades.' },
        { key: 'trading.xpForTrade', value: '5', description: 'Villager XP per trade.' },
        { key: 'trading.priceMultiplierForTradeFluctuation', value: '0.05', description: 'Demand price multiplier.' },
      ],
    },
    {
      id: 'cat-stats',
      title: 'Cat stats',
      rows: [
        { key: 'catStats.wildMaxHealth', value: '20.0', description: 'Wild cat max health.' },
        { key: 'catStats.tamedMinHealth', value: '20.0', description: 'Minimum tamed cat max health.' },
        { key: 'catStats.tamedMaxHealth', value: '35.0', description: 'Maximum tamed cat max health.' },
        { key: 'catStats.tamedDefaultSpeed', value: '0.4', description: 'Base movement speed for tamed cats.' },
        { key: 'catStats.affinityLevelOne', value: '12000', description: 'Affinity XP threshold for level 1.' },
        { key: 'catStats.affinityLevelTwo', value: '48000', description: 'Affinity XP threshold for level 2.' },
        { key: 'catStats.affinityLevelThree', value: '192000', description: 'Affinity XP threshold for level 3.' },
        { key: 'catStats.affinityLevelFour', value: '768000', description: 'Affinity XP threshold for level 4.' },
        { key: 'catStats.affinityLevelFive', value: '3072000', description: 'Affinity XP threshold for level 5.' },
        { key: 'catStats.bondLevelOne', value: '12000', description: 'Bond XP threshold for level 1.' },
        { key: 'catStats.bondLevelTwo', value: '48000', description: 'Bond XP threshold for level 2.' },
        { key: 'catStats.bondLevelThree', value: '192000', description: 'Bond XP threshold for level 3.' },
        { key: 'catStats.bondLevelFour', value: '768000', description: 'Bond XP threshold for level 4.' },
        { key: 'catStats.bondLevelFive', value: '3072000', description: 'Bond XP threshold for level 5.' },
        { key: 'catStats.nonFavoriteFoodBondXp', value: '1', description: 'Bond XP from non-favorite food.' },
        { key: 'catStats.favoriteFoodBondXp', value: '2', description: 'Bond XP from favorite food.' },
        { key: 'catStats.interactionBondXpMin', value: '1', description: 'Minimum bond XP from petting.' },
        { key: 'catStats.interactionBondXpMax', value: '3', description: 'Maximum bond XP from petting.' },
        { key: 'catStats.proximityBondXp', value: '1', description: 'Bond XP from proximity checks.' },
        { key: 'catStats.largeBondProximityDistance', value: '30.0', description: 'Large proximity distance.' },
        { key: 'catStats.mediumBondProximityDistance', value: '20.0', description: 'Medium proximity distance.' },
        { key: 'catStats.smallBondProximityDistance', value: '10.0', description: 'Small proximity distance.' },
        { key: 'catStats.largeProximityBondIncreaseChance', value: '0.10', description: 'Chance at large distance.' },
        { key: 'catStats.mediumProximityBondIncreaseChance', value: '0.30', description: 'Chance at medium distance.' },
        { key: 'catStats.smallProximityBondIncreaseChance', value: '0.90', description: 'Chance at small distance.' },
      ],
    },
    {
      id: 'behavior',
      title: 'Behavior',
      rows: [
        { key: 'behavior.preventOwnerFriendlyFireToCat', value: 'true', description: 'Block owner damage to cats.' },
        { key: 'behavior.preventAnyFriendlyFireToCat', value: 'false', description: 'Block all player damage to cats.' },
        { key: 'behavior.preventNonOwnersFromBreeding', value: 'true', description: 'Only owners can breed cats.' },
        { key: 'behavior.babyCatsAlwaysWild', value: 'true', description: 'Kittens spawn untamed.' },
        { key: 'behavior.tamedFollowStopDistance', value: '1.0', description: 'Stop distance for follow goal.' },
        {
          key: 'behavior.additionalBlocksFromWanderToTeleport',
          value: '3.0',
          description: 'Extra blocks before teleporting.',
        },
        { key: 'behavior.emoteChance', value: '0.0001667', description: 'Per-tick emote chance.' },
        { key: 'behavior.ownerBondEmoteChance', value: '0.25', description: 'Bond emote chance.' },
        { key: 'behavior.emoteMaxDistance', value: '30.0', description: 'Max distance for owner emotes.' },
        { key: 'behavior.pairedActionRange', value: '7.0', description: 'Range for paired cat emotes.' },
        { key: 'behavior.enableSoloEmotes', value: 'true', description: 'Enable solo emotes.' },
        { key: 'behavior.enablePairedEmotes', value: 'true', description: 'Enable paired emotes.' },
        { key: 'behavior.abilityRange', value: '25.0', description: 'Max distance for class abilities.' },
      ],
    },
    {
      id: 'toggles',
      title: 'Feature toggles',
      rows: [
        { key: 'toggles.teleportSoundEnabled', value: 'true', description: 'Play teleport sound effects.' },
        { key: 'toggles.genderSystemEnabled', value: 'true', description: 'Enable gender-based class rolls.' },
        { key: 'toggles.reviveEnabled', value: 'true', description: 'Enable cat collar revives.' },
        { key: 'toggles.allowVanillaTaming', value: 'false', description: 'Allow cod and salmon taming.' },
        { key: 'toggles.enableFallDamage', value: 'false', description: 'Allow fall damage to cats.' },
        { key: 'toggles.enableTotemReroll', value: 'false', description: 'Allow Totem of Undying rerolls.' },
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      rows: [
        { key: 'notifs.catRevivedNotif', value: 'true', description: 'Notify when a cat is revived.' },
        { key: 'notifs.newCatGetNotif', value: 'true', description: 'Notify when a cat is tamed.' },
        { key: 'notifs.catAbilityNotif', value: 'true', description: 'Notify when an ability triggers.' },
        { key: 'notifs.catInteractionProgressNotif', value: 'true', description: 'Notify petting progress.' },
        { key: 'notifs.catInteractionCompletedNotif', value: 'true', description: 'Notify petting completion.' },
        {
          key: 'notifs.catNonOwnerInteractionCompletedNotif',
          value: 'true',
          description: 'Notify owner when others finish petting.',
        },
        { key: 'notifs.tamingHintNotif', value: 'true', description: 'Show taming hint message.' },
        { key: 'notifs.catHealthFeedNotif', value: 'true', description: 'Show health bar after feeding.' },
      ],
    },
    {
      id: 'misc',
      title: 'Misc',
      rows: [
        { key: 'misc.shouldAlwaysRenderName', value: 'true', description: 'Always render nameplates.' },
        { key: 'misc.tameChance', value: '2', description: '1 in X chance to tame.' },
        { key: 'misc.statusEffectDurationSeconds', value: '10', description: 'Ability effect duration.' },
        { key: 'misc.secondsToInteract', value: '5', description: 'Seconds between petting ticks.' },
        { key: 'misc.interactionsToCompletion', value: '3', description: 'Petting ticks to complete.' },
        { key: 'misc.trainingStringDurability', value: '99', description: 'Training String durability.' },
        { key: 'misc.heartIconHealthAmount', value: '4', description: 'Health per heart icon.' },
        { key: 'misc.foodHealAmount', value: '4', description: 'Base heal from feeding.' },
        { key: 'misc.favoriteFoodHealMultiplier', value: '2', description: 'Favorite food heal multiplier.' },
        { key: 'misc.healthDecayAmount', value: '1', description: 'Health lost per decay tick.' },
        { key: 'misc.healthDecayIntervalTicks', value: '1500', description: 'Ticks between decay checks.' },
        { key: 'misc.minHealthPercentage', value: '0.5', description: 'Minimum health percent after decay.' },
      ],
    },
    {
      id: 'ancient-fish',
      title: 'Ancient fish',
      rows: [
        { key: 'ancientFish.ancientSalmonDropChance', value: '0.5', description: 'Chance for Salmon to drop Ancient Salmon.' },
        { key: 'ancientFish.ancientCodDropChance', value: '0.5', description: 'Chance for Cod to drop Ancient Cod.' },
        { key: 'ancientFish.experienceForSmeltingAncientFish', value: '1.0', description: 'Smelting XP for ancient fish.' },
        { key: 'ancientFish.ticksToSmeltAncientFish', value: '200', description: 'Smelting time for ancient fish.' },
        { key: 'ancientFish.rawAncientFishNutritionAmount', value: '2', description: 'Hunger restored by raw ancient fish.' },
        { key: 'ancientFish.rawAncientFishSaturationModifier', value: '0.1', description: 'Saturation from raw ancient fish.' },
        { key: 'ancientFish.cookedAncientFishNutritionAmount', value: '5', description: 'Hunger restored by cooked ancient fish.' },
        { key: 'ancientFish.cookedAncientFishSaturationModifier', value: '0.6', description: 'Saturation from cooked ancient fish.' },
        { key: 'ancientFish.cookedAncientFishEffects', value: 'true', description: 'Enable cooked ancient fish effects.' },
      ],
    },
    {
      id: 'classes',
      title: 'Classes',
      rows: [
        { key: 'classes.maleChanceForLucky', value: '0.05', description: 'Legacy male Lucky chance.' },
        { key: 'classes.maleChanceForDreamer', value: '0.15', description: 'Legacy male Dreamer chance.' },
        { key: 'classes.maleChanceForGuardian', value: '0.25', description: 'Legacy male Guardian chance.' },
        { key: 'classes.maleChanceForNaiad', value: '0.25', description: 'Legacy male Naiad chance.' },
        { key: 'classes.maleChanceForWitchling', value: '0.20', description: 'Legacy male Witchling chance.' },
        { key: 'classes.femaleChanceForLucky', value: '0.05', description: 'Legacy female Lucky chance.' },
        { key: 'classes.femaleChanceForDreamer', value: '0.15', description: 'Legacy female Dreamer chance.' },
        { key: 'classes.femaleChanceForGuardian', value: '0.20', description: 'Legacy female Guardian chance.' },
        { key: 'classes.femaleChanceForNaiad', value: '0.35', description: 'Legacy female Naiad chance.' },
        { key: 'classes.femaleChanceForWitchling', value: '0.25', description: 'Legacy female Witchling chance.' },
        {
          key: 'classes.guardianCreeperDetectionRadius',
          value: '15',
          description: 'Guardian creeper detection radius.',
        },
        {
          key: 'classes.guardianPhantomDetectionRadius',
          value: '200',
          description: 'Guardian phantom detection radius.',
        },
        { key: 'classes.witchlingGiftChance', value: '0.0001667', description: 'Witchling gift chance per tick.' },
      ],
    },
    {
      id: 'gender-class-chances',
      title: 'Gender class chances',
      rows: [
        { key: 'genderClassChances.maleChances.lucky', value: '0.05', description: 'Male Lucky chance.' },
        { key: 'genderClassChances.maleChances.dreamer', value: '0.15', description: 'Male Dreamer chance.' },
        { key: 'genderClassChances.maleChances.guardian', value: '0.35', description: 'Male Guardian chance.' },
        { key: 'genderClassChances.maleChances.naiad', value: '0.25', description: 'Male Naiad chance.' },
        { key: 'genderClassChances.maleChances.witchling', value: '0.20', description: 'Male Witchling chance.' },
        { key: 'genderClassChances.femaleChances.lucky', value: '0.05', description: 'Female Lucky chance.' },
        { key: 'genderClassChances.femaleChances.dreamer', value: '0.15', description: 'Female Dreamer chance.' },
        { key: 'genderClassChances.femaleChances.guardian', value: '0.20', description: 'Female Guardian chance.' },
        { key: 'genderClassChances.femaleChances.naiad', value: '0.35', description: 'Female Naiad chance.' },
        { key: 'genderClassChances.femaleChances.witchling', value: '0.25', description: 'Female Witchling chance.' },
      ],
    },
    {
      id: 'debug',
      title: 'Debug',
      rows: [
        { key: 'debug.enableNbtWarnings', value: 'true', description: 'Log warnings for bad NBT data.' },
        { key: 'debug.debugLoggingEnabled', value: 'false', description: 'Enable debug logging.' },
      ],
    },
  ];

  const wolvesConfigSections: ConfigSection[] = [
    {
      id: 'trading',
      title: 'Trading',
      rows: [
        { key: 'trading.ancientBoneEmeraldCost', value: '3', description: 'Emerald cost for Ancient Bone trades.' },
        { key: 'trading.ancientBoneTradeReceive', value: '1', description: 'Ancient Bones per trade.' },
        { key: 'trading.clericLevelForTrade', value: '3', description: 'Cleric level required for trades.' },
      ],
    },
    {
      id: 'wolf-stats',
      title: 'Wolf stats',
      rows: [
        { key: 'wolfStats.wildWolfMaxHealth', value: '15.0', description: 'Wild wolf max health.' },
        { key: 'wolfStats.wildWolfAttackDamage', value: '4.0', description: 'Wild wolf attack damage.' },
        { key: 'wolfStats.wildWolfMovementSpeed', value: '0.4', description: 'Wild wolf movement speed.' },
        { key: 'wolfStats.tamedMinAttack', value: '2.0', description: 'Minimum tamed wolf attack.' },
        { key: 'wolfStats.tamedMaxAttack', value: '6.0', description: 'Maximum tamed wolf attack.' },
        { key: 'wolfStats.tamedMinHealth', value: '20.0', description: 'Minimum tamed wolf health.' },
        { key: 'wolfStats.tamedMaxHealth', value: '35.0', description: 'Maximum tamed wolf health.' },
        { key: 'wolfStats.tamedMinSpeed', value: '0.3', description: 'Minimum tamed wolf speed.' },
        { key: 'wolfStats.tamedMaxSpeed', value: '0.4', description: 'Maximum tamed wolf speed.' },
        { key: 'wolfStats.tamedGathererMinGiftChance', value: '0.0', description: 'Minimum gatherer gift chance.' },
        { key: 'wolfStats.tamedGathererMaxGiftChance', value: '0.0025', description: 'Maximum gatherer gift chance.' },
        { key: 'wolfStats.tamedHunterMinGiftChance', value: '0.0', description: 'Minimum hunter gift chance.' },
        { key: 'wolfStats.tamedHunterMaxGiftChance', value: '0.4', description: 'Maximum hunter gift chance.' },
        { key: 'wolfStats.tamedMinWanderRange', value: '5.0', description: 'Minimum wander range.' },
        { key: 'wolfStats.tamedMaxWanderRange', value: '12.0', description: 'Maximum wander range.' },
        { key: 'wolfStats.hunterMinAttack', value: '3.0', description: 'Hunter min attack.' },
        { key: 'wolfStats.hunterMaxAttack', value: '6.0', description: 'Hunter max attack.' },
        { key: 'wolfStats.hunterMinHealth', value: '25.0', description: 'Hunter min health.' },
        { key: 'wolfStats.hunterMaxHealth', value: '35.0', description: 'Hunter max health.' },
        { key: 'wolfStats.hunterMinSpeed', value: '0.3', description: 'Hunter min speed.' },
        { key: 'wolfStats.hunterMaxSpeed', value: '0.36', description: 'Hunter max speed.' },
        { key: 'wolfStats.hunterGiftChanceMin', value: '0.0', description: 'Hunter gift chance min.' },
        { key: 'wolfStats.hunterGiftChanceMax', value: '0.2', description: 'Hunter gift chance max.' },
        { key: 'wolfStats.hunterMinWanderRange', value: '5.0', description: 'Hunter min wander range.' },
        { key: 'wolfStats.hunterMaxWanderRange', value: '9.0', description: 'Hunter max wander range.' },
        { key: 'wolfStats.gathererMinAttack', value: '2.0', description: 'Gatherer min attack.' },
        { key: 'wolfStats.gathererMaxAttack', value: '4.0', description: 'Gatherer max attack.' },
        { key: 'wolfStats.gathererMinHealth', value: '20.0', description: 'Gatherer min health.' },
        { key: 'wolfStats.gathererMaxHealth', value: '28.0', description: 'Gatherer max health.' },
        { key: 'wolfStats.gathererMinSpeed', value: '0.33', description: 'Gatherer min speed.' },
        { key: 'wolfStats.gathererMaxSpeed', value: '0.4', description: 'Gatherer max speed.' },
        { key: 'wolfStats.gathererGiftChanceMin', value: '0.00125', description: 'Gatherer gift chance min.' },
        { key: 'wolfStats.gathererGiftChanceMax', value: '0.0025', description: 'Gatherer gift chance max.' },
        { key: 'wolfStats.gathererMinWanderRange', value: '8.0', description: 'Gatherer min wander range.' },
        { key: 'wolfStats.gathererMaxWanderRange', value: '12.0', description: 'Gatherer max wander range.' },
      ],
    },
    {
      id: 'behavior',
      title: 'Behavior',
      rows: [
        { key: 'behavior.preventOwnerFriendlyFireToWolf', value: 'true', description: 'Block owner damage to wolves.' },
        { key: 'behavior.preventAnyFriendlyFireToWolf', value: 'false', description: 'Block all player damage to wolves.' },
        { key: 'behavior.preventNonOwnersFromBreeding', value: 'true', description: 'Only owners can breed wolves.' },
        { key: 'behavior.babyWolvesAlwaysWild', value: 'true', description: 'Pups spawn untamed.' },
        { key: 'behavior.enableTeleportSound', value: 'true', description: 'Play teleport sound effects.' },
        { key: 'behavior.enableGenderSystem', value: 'true', description: 'Enable gender-based rolls.' },
        { key: 'behavior.allowFallDamage', value: 'true', description: 'Allow fall damage for wolves.' },
        { key: 'behavior.enableTotemReroll', value: 'false', description: 'Allow Totem of Undying respec.' },
        { key: 'behavior.tamedFollowStopDistance', value: '1.0', description: 'Stop distance for follow goal.' },
        {
          key: 'behavior.additionalBlocksFromWanderToTeleport',
          value: '3.0',
          description: 'Extra blocks before teleporting.',
        },
        { key: 'behavior.emoteChance', value: '0.0001667', description: 'Per-tick emote chance.' },
        { key: 'behavior.emoteMaxDistance', value: '30.0', description: 'Max distance for owner emotes.' },
        { key: 'behavior.pairedActionRange', value: '7.0', description: 'Range for paired emotes.' },
        { key: 'behavior.enableSoloEmotes', value: 'true', description: 'Enable solo emotes.' },
        { key: 'behavior.enablePairedEmotes', value: 'true', description: 'Enable paired emotes.' },
        { key: 'behavior.gathererGiftOwnerRange', value: '20.0', description: 'Gift range for gatherers.' },
        { key: 'behavior.enableGathererGift', value: 'true', description: 'Enable gatherer gift digging.' },
        { key: 'behavior.enableHunterGift', value: 'true', description: 'Enable hunter gift on kills.' },
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      rows: [
        { key: 'notifs.wolfKilledMobNotif', value: 'true', description: 'Notify when a wolf gets a kill.' },
        { key: 'notifs.wolfRevivedNotif', value: 'true', description: 'Notify when a wolf is revived.' },
        { key: 'notifs.newWolfGetNotif', value: 'true', description: 'Notify when a wolf is tamed.' },
        { key: 'notifs.wolfGiftNotif', value: 'true', description: 'Notify when a gift is found.' },
        { key: 'notifs.wolfInteractionProgressNotif', value: 'true', description: 'Notify petting progress.' },
        { key: 'notifs.wolfInteractionCompletedNotif', value: 'true', description: 'Notify petting completion.' },
        {
          key: 'notifs.wolfNonOwnerInteractionCompletedNotif',
          value: 'true',
          description: 'Notify owners when others finish petting.',
        },
        { key: 'notifs.tamingHintNotif', value: 'true', description: 'Show taming hint message.' },
        { key: 'notifs.wolfHealthFeedNotif', value: 'true', description: 'Show health bar after feeding.' },
      ],
    },
    {
      id: 'misc',
      title: 'Misc',
      rows: [
        { key: 'misc.ancientBoneSkeletonDropChance', value: '0.10', description: 'Skeleton drop chance for Ancient Bone.' },
        { key: 'misc.shouldAlwaysRenderName', value: 'true', description: 'Always render nameplates.' },
        { key: 'misc.tameChance', value: '2', description: '1 in X chance to tame.' },
        { key: 'misc.allowVanillaTaming', value: 'false', description: 'Allow bones to tame wolves.' },
        { key: 'misc.statusEffectDurationSeconds', value: '10', description: 'Effect duration in seconds.' },
        { key: 'misc.secondsToInteract', value: '5', description: 'Seconds between petting ticks.' },
        { key: 'misc.interactionsToCompletion', value: '3', description: 'Petting ticks to complete.' },
        { key: 'misc.trainingStickDurability', value: '99', description: 'Training Stick durability.' },
        { key: 'misc.enableReviveMechanics', value: 'true', description: 'Enable collar drop + revive.' },
        { key: 'misc.maleChanceForHunter', value: '0.7', description: 'Male chance to roll Hunter.' },
        { key: 'misc.femaleChanceForGatherer', value: '0.7', description: 'Female chance to roll Gatherer.' },
      ],
    },
    {
      id: 'debug',
      title: 'Debug',
      rows: [
        { key: 'debug.enableDebugLogging', value: 'true', description: 'Enable debug logging.' },
        { key: 'debug.enableNbtWarnings', value: 'true', description: 'Log warnings for bad NBT data.' },
      ],
    },
  ];

  const pages: Record<string, Page> = {
    home: {
      id: 'home',
      title: "Cozy's Improved Mods Wiki",
      group: 'home',
      summary: "Cozy, player-friendly guides for Cozy's Improved Cats and Cozy's Improved Wolves.",
      tags: [
        { label: 'Improved Cats', tone: 'accent' },
        { label: 'Improved Wolves', tone: 'info' },
      ],
      quickRef: [
        { label: 'Cats Mod', value: `v${catsModVersion}` },
        { label: 'Wolves Mod', value: `v${wolvesModVersion}` },
        { label: 'Supported MC', value: catsSupportedVersions.join(', ') },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Cozy's Improved Cats and Wolves turn vanilla companions into full-on partners with stats, classes,
                revive tools, and quality-of-life upgrades. Everything here is checked against Cats Mod v{catsModVersion}{' '}
                and Wolves Mod v{wolvesModVersion}.
              </p>
              <div className="card-grid">
                <div className="card mod-card">
                  <h3 className="mod-title">
                    <img src="/assets/items/cat_spawn_egg.png" alt="Cat spawn egg" className="mod-icon" />
                    Improved Cats
                  </h3>
                  <p>
                    Learn how to tame, care for, and power up your cats. Full config reference, item guides, and the
                    complete changelog included.
                  </p>
                  <div className="card-actions">
                    <PageLink pageId="cats-getting-started">Start here</PageLink>
                    <PageLink pageId="cats-changelog">Full changelog</PageLink>
                  </div>
                </div>
                <div className="card mod-card">
                  <h3 className="mod-title">
                    <img src="/assets/items/wolf_spawn_egg.png" alt="Wolf spawn egg" className="mod-icon" />
                    Improved Wolves
                  </h3>
                  <p>
                    Tame, train, and revive wolves with Ancient Bones, class rolls, and gift hunting. Now fully
                    documented for v{wolvesModVersion}.
                  </p>
                  <div className="card-actions">
                    <PageLink pageId="wolves-getting-started">Start here</PageLink>
                    <PageLink pageId="wolves-changelog">Full changelog</PageLink>
                  </div>
                </div>
              </div>
            </>
          ),
        },
        {
          id: 'jump-in',
          title: 'Jump in',
          content: (
            <ul>
              <li>
                <PageLink pageId="cats-getting-started">Cats getting started</PageLink>
              </li>
              <li>
                <PageLink pageId="cats-items">Cats items and crafting</PageLink>
              </li>
              <li>
                <PageLink pageId="wolves-getting-started">Wolves getting started</PageLink>
              </li>
              <li>
                <PageLink pageId="wolves-items">Wolves items and crafting</PageLink>
              </li>
              <li>
                <PageLink pageId="shared-troubleshooting">Troubleshooting</PageLink>
              </li>
            </ul>
          ),
        },
        {
          id: 'latest',
          title: 'Latest update',
          content: (
            <Callout tone="tip" title="Cats v1.3.0 + Wolves v1.4.0 are live">
              New toggles, Cloth Config menus, and the Totem respec option are in for both mods.{' '}
              <PageLink pageId="cats-changelog">Cats changelog</PageLink> /{' '}
              <PageLink pageId="wolves-changelog">Wolves changelog</PageLink>.
            </Callout>
          ),
        },
      ],
    },
    'cats-getting-started': {
      id: 'cats-getting-started',
      title: 'Getting Started',
      group: 'cats',
      version: catsModVersion,
      summary: 'Quick setup, taming basics, and where to grab the new items.',
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Getting started', tone: 'info' },
      ],
      quickRef: [
        { label: 'Cats Mod', value: `v${catsModVersion}` },
        { label: 'Supported MC', value: catsSupportedVersions.join(', ') },
        { label: 'Config file', value: 'config/cozys-improved-cats.json' },
        { label: 'Reload command', value: '/reloadcozycatsconfig' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Cozy's Improved Cats upgrades vanilla cats into proper companions with classes, bond, affinity, and
                revival. Existing tamed cats are safely adopted when the mod loads.
              </p>
              <Callout tone="info" title="Automatic upgrades">
                Your current cats keep their owner, name, collar color, and variant when they convert.
              </Callout>
            </>
          ),
        },
        {
          id: 'install',
          title: 'Install and launch',
          content: (
            <ol>
              <li>Install Fabric Loader and Fabric API for your Minecraft version.</li>
              <li>Drop the Cozy's Improved Cats jar in your mods folder.</li>
              <li>Optional but recommended: install Mod Menu + Cloth Config for the in-game settings screen.</li>
              <li>Launch the game and start taming.</li>
            </ol>
          ),
        },
        {
          id: 'taming-basics',
          title: 'Taming basics',
          content: (
            <ul>
              <li>Taming uses Ancient Cod or Ancient Salmon by default.</li>
              <li>Ancient fish drop from cod and salmon or show up in Fisherman trades.</li>
              <li>Enable vanilla taming in the config if you want regular cod or salmon to work.</li>
              <li>Use a Training String (sneak + right click) to see stats.</li>
            </ul>
          ),
        },
        {
          id: 'supported-versions',
          title: 'Supported Minecraft versions',
          content: (
            <>
              <p>Cats Mod v{catsModVersion} supports these Minecraft versions:</p>
              <VersionChips versions={catsSupportedVersions} />
            </>
          ),
        },
        {
          id: 'config-basics',
          title: 'Config basics',
          content: (
            <ul>
              <li>Config file: <code>config/cozys-improved-cats.json</code>.</li>
              <li>Use <code>/reloadcozycatsconfig</code> to reload without restarting.</li>
              <li>Toggle gender, revive, fall damage, and more in the config.</li>
            </ul>
          ),
        },
        {
          id: 'related-links',
          title: 'Related links',
          content: (
            <ul>
              <li>
                <PageLink pageId="cats-items">Items and crafting</PageLink>
              </li>
              <li>
                <PageLink pageId="cats-config">Config reference</PageLink>
              </li>
              <li>
                <PageLink pageId="cats-changelog">Full changelog</PageLink>
              </li>
            </ul>
          ),
        },
      ],
    },
    'cats-features': {
      id: 'cats-features',
      title: 'Features Overview',
      group: 'cats',
      version: catsModVersion,
      summary: 'The big picture: what changes, what you get, and why cats feel alive.',
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Features', tone: 'info' },
      ],
      quickRef: [
        { label: 'Taming items', value: 'Ancient Cod, Ancient Salmon' },
        { label: 'Classes', value: 'Naiad, Guardian, Witchling, Dreamer, Lucky' },
        { label: 'Revival', value: 'Cat Collar (owner only)' },
        { label: 'Progression', value: 'Bond + Affinity' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Improved Cats adds real companion depth to vanilla cats. Every tame rolls stats, a class, a favorite
                food, and two progression tracks (bond and affinity). Abilities scale with affinity and fire
                automatically while you play.
              </p>
              <Callout tone="tip" title="Quick stat check">
                Use a Training String (sneak + right click) to show the cat stat bars, deaths, and ability count.
              </Callout>
            </>
          ),
        },
        {
          id: 'core-features',
          title: 'Core features',
          content: (
            <ul>
              <li>Class system: Naiad, Guardian, Witchling, Dreamer, Lucky.</li>
              <li>Stat rolls on tame, plus trackers for deaths and ability usage.</li>
              <li>Cat Collar revival (owner only, config toggle).</li>
              <li>Owner-only breeding with optional wild kittens.</li>
              <li>Gender system (optional) that influences class rolls and name colors.</li>
            </ul>
          ),
        },
        {
          id: 'progression',
          title: 'Progression you can feel',
          content: (
            <ul>
              <li>Affinity powers class abilities and grows from class-specific actions.</li>
              <li>Bond grows by petting, feeding, and keeping cats close.</li>
              <li>Favorite food gives extra healing and bond when you discover it.</li>
            </ul>
          ),
        },
        {
          id: 'quality-of-life',
          title: 'Quality-of-life upgrades',
          content: (
            <ul>
              <li>Petting interactions with progress and heart particles.</li>
              <li>Emotes that react to time, weather, and bond.</li>
              <li>Teleport sound + soul particles on cat teleport.</li>
              <li>Health decay system that stops above a minimum percent.</li>
              <li>Cats follow their owner when holding valid food.</li>
            </ul>
          ),
        },
        {
          id: 'config-highlights',
          title: 'Config highlights',
          content: (
            <ul>
              <li>Allow vanilla taming, toggle revival, and enable fall damage.</li>
              <li>Adjust class roll weights and stat ranges.</li>
              <li>Enable Totem respec to reroll class and stats.</li>
              <li>See <PageLink pageId="cats-config">Config reference</PageLink> for all options.</li>
            </ul>
          ),
        },
      ],
    },
    'cats-classes': {
      id: 'cats-classes',
      title: 'Cat Classes and Abilities',
      group: 'cats',
      version: catsModVersion,
      summary: 'Each class has its own vibe, ability trigger, and affinity style.',
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Classes', tone: 'info' },
      ],
      quickRef: [
        { label: 'Affinity levels', value: '0 to 5' },
        { label: 'Ability range', value: 'behavior.abilityRange' },
        { label: 'Class roll weights', value: 'genderClassChances.*' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                On tame, every cat rolls a class. If the gender system is enabled, class chances are weighted per
                gender. If disabled, the mod uses a neutral average of both tables.
              </p>
              <Callout tone="info" title="Ability timing">
                Naiad and Guardian abilities run on cooldowns that shrink with higher affinity. Dreamer, Lucky, and
                Witchling are passive and trigger while the cat is active.
              </Callout>
            </>
          ),
        },
        {
          id: 'how-it-works',
          title: 'How it works',
          content: (
            <ul>
              <li>Keep cats close and active to increase affinity for their class.</li>
              <li>Abilities trigger automatically, no manual activation required.</li>
              <li>Use <code>behavior.abilityRange</code> to control how far the owner can be.</li>
            </ul>
          ),
        },
        {
          id: 'class-breakdown',
          title: 'Class breakdown',
          content: (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Ability</th>
                    <th>Affinity gain</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Naiad</td>
                    <td>Regeneration when owner health is low.</td>
                    <td>While touching water or under open sky.</td>
                    <td>Cooldown scales with affinity level.</td>
                  </tr>
                  <tr>
                    <td>Guardian</td>
                    <td>Resistance when owner takes damage.</td>
                    <td>When hostile mobs are nearby.</td>
                    <td>Cat glows near creepers or phantoms.</td>
                  </tr>
                  <tr>
                    <td>Witchling</td>
                    <td>Occasional witchy gifts.</td>
                    <td>Near brewing stands or when owner has a status effect (excluding Luck).</td>
                    <td>Gift chance is configurable.</td>
                  </tr>
                  <tr>
                    <td>Dreamer</td>
                    <td>Stores XP and grants Bottles o' Enchanting.</td>
                    <td>While not sitting.</td>
                    <td>Gift amount scales with affinity.</td>
                  </tr>
                  <tr>
                    <td>Lucky</td>
                    <td>Grants Luck I while nearby.</td>
                    <td>While not sitting.</td>
                    <td>Effect refreshes automatically.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ),
        },
        {
          id: 'affinity-tips',
          title: 'Affinity tips',
          content: (
            <ul>
              <li>Naiad: let them splash or hang out under open sky.</li>
              <li>Guardian: keep them near fights so hostile mobs are nearby.</li>
              <li>Witchling: stand by brewing stands or stay buffed.</li>
              <li>Dreamer and Lucky: keep them standing and traveling with you.</li>
            </ul>
          ),
        },
        {
          id: 'tune-it',
          title: 'Tune it',
          content: (
            <ul>
              <li>Adjust class roll weights under <code>genderClassChances</code>.</li>
              <li>Guardian detection radii and Witchling gift chance live under <code>classes</code>.</li>
              <li>See <PageLink pageId="cats-config">Config reference</PageLink> for the full list.</li>
            </ul>
          ),
        },
      ],
    },
    'cats-items': {
      id: 'cats-items',
      title: 'Items and Crafting',
      group: 'cats',
      version: catsModVersion,
      summary: 'Ancient fish, training tools, and revival gear you will actually use.',
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Items', tone: 'info' },
      ],
      quickRef: [
        { label: 'Taming', value: 'Ancient Cod or Ancient Salmon' },
        { label: 'Training String', value: 'Sneak + right click to inspect' },
        { label: 'Cat Collar', value: 'Revive on block (owner only)' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Cozy's Improved Cats adds ancient fish plus two utility items. Ancient fish tame cats and can be cooked
                for bonus effects. The Training String inspects stats, and the Cat Collar revives fallen cats.
              </p>
              <Callout tone="tip" title="Item glint">
                Ancient fish, Training String, and Cat Collar always render with a glint so they are easy to spot.
              </Callout>
            </>
          ),
        },
        {
          id: 'item-gallery',
          title: 'Item gallery',
          content: (
            <div className="item-grid">
              <ItemCard
                icon="ancient_cod"
                title="Ancient Cod"
                lines={[
                  'Primary taming item for Improved Cats.',
                  'Drops from cod (chance is configurable).',
                  'Same hunger and saturation as raw cod.',
                ]}
              />
              <ItemCard
                icon="ancient_salmon"
                title="Ancient Salmon"
                lines={[
                  'Primary taming item for Improved Cats.',
                  'Drops from salmon (chance is configurable).',
                  'Same hunger and saturation as raw salmon.',
                ]}
              />
              <ItemCard
                icon="ancient_cooked_cod"
                title="Ancient Cooked Cod"
                lines={[
                  'Smelt or blast Ancient Cod.',
                  'Can grant Luck, Regeneration, Resistance, or XP.',
                  'Bonus effects can be toggled in config.',
                ]}
              />
              <ItemCard
                icon="ancient_cooked_salmon"
                title="Ancient Cooked Salmon"
                lines={[
                  'Smelt or blast Ancient Salmon.',
                  'Can grant Luck, Regeneration, Resistance, or XP.',
                  'Bonus effects can be toggled in config.',
                ]}
              />
              <ItemCard
                icon="training_string"
                title="Training String"
                lines={[
                  'Shapeless craft: String + Ancient Cod + Ancient Salmon.',
                  'Sneak + right click a tamed cat to show stats.',
                  'Durability defaults to 99 uses.',
                ]}
              />
              <ItemCard
                icon="cat_collar"
                title="Cat Collar"
                lines={[
                  'Created when a tamed cat dies (if enabled).',
                  'Use on a block to revive that cat.',
                  'Owner only, consumed on use.',
                ]}
              />
            </div>
          ),
        },
        {
          id: 'getting-items',
          title: 'Getting the items',
          content: (
            <ul>
              <li>Ancient fish drop from cod and salmon based on a configurable chance.</li>
              <li>Fisherman villagers can trade Ancient fish for emeralds (level is configurable).</li>
              <li>Smelting or blasting ancient fish makes cooked versions with configurable XP.</li>
              <li>Training String is a shapeless craft with both ancient fish plus string.</li>
            </ul>
          ),
        },
        {
          id: 'tune-it',
          title: 'Tune it',
          content: (
            <ul>
              <li>Ancient fish drop chance and nutrition values are in <code>ancientFish</code>.</li>
              <li>Cooked fish effects are toggled by <code>ancientFish.cookedAncientFishEffects</code>.</li>
              <li>Training String durability is <code>misc.trainingStringDurability</code>.</li>
              <li>Trade settings live under <code>trading</code>.</li>
            </ul>
          ),
        },
      ],
    },
    'cats-interactions': {
      id: 'cats-interactions',
      title: 'Interactions, Bond, and Emotes',
      group: 'cats',
      version: catsModVersion,
      summary: 'Petting, feeding, bond growth, and all the little cat vibes.',
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Interactions', tone: 'info' },
      ],
      quickRef: [
        { label: 'Petting', value: 'Sneak + right click with empty hand' },
        { label: 'Favorite food', value: 'Revealed on first feed' },
        { label: 'Emotes', value: 'Solo and paired, context aware' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Interactions are all about bonding. Feeding keeps cats healthy and reveals favorite food. Petting builds
                bond through timed interaction ticks. Emotes add personality based on time, weather, and bond level.
              </p>
              <Callout tone="info" title="Bond vs affinity">
                Bond represents closeness with the owner and drives owner emotes. Affinity represents class progression
                and powers abilities.
              </Callout>
            </>
          ),
        },
        {
          id: 'petting',
          title: 'Petting and bond',
          content: (
            <ul>
              <li>Sneak + right click with an empty hand to pet.</li>
              <li>Repeat until the progress bar finishes to gain bond XP.</li>
              <li>Keep cats close for extra passive bond gain.</li>
            </ul>
          ),
        },
        {
          id: 'feeding',
          title: 'Feeding and favorite food',
          content: (
            <ul>
              <li>Feed cats cod, salmon, chicken, beef, porkchop, mutton, rabbit, or ancient fish.</li>
              <li>Favorite food heals more and boosts bond XP once discovered.</li>
              <li>Feeding shows a heart bar to confirm healing.</li>
            </ul>
          ),
        },
        {
          id: 'emotes',
          title: 'Emotes and personality',
          content: (
            <ul>
              <li>Emotes fire based on time, weather, sitting, and bond.</li>
              <li>Paired emotes happen when other tamed cats are nearby.</li>
              <li>Owner emotes get sweeter as bond climbs.</li>
            </ul>
          ),
        },
        {
          id: 'tune-it',
          title: 'Tune it',
          content: (
            <ul>
              <li>Interaction timing lives under <code>misc.secondsToInteract</code>.</li>
              <li>Bond XP settings live under <code>catStats</code>.</li>
              <li>Emote settings live under <code>behavior</code>.</li>
              <li>See <PageLink pageId="cats-config">Config reference</PageLink> for all options.</li>
            </ul>
          ),
        },
      ],
    },
    'cats-revival': {
      id: 'cats-revival',
      title: 'Revival and Soul Transfiguration',
      group: 'cats',
      version: catsModVersion,
      summary: 'Bring cats back with a Cat Collar, or reroll with a Totem if you want a fresh start.',
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Revival', tone: 'info' },
      ],
      quickRef: [
        { label: 'Cat Collar drop', value: 'On tamed cat death' },
        { label: 'Revive', value: 'Use collar on a block' },
        { label: 'Totem reroll', value: 'Optional config toggle' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                When a tamed cat dies, the mod creates a Cat Collar containing its data. Using that collar on a block
                revives the same cat with its stored stats and identity.
              </p>
              <p>
                A separate feature called Soul Transfiguration lets you use a Totem of Undying on a tamed cat to reroll
                its class, gender, and stats, with a penalty to bond and affinity.
              </p>
            </>
          ),
        },
        {
          id: 'revival-flow',
          title: 'Revival flow',
          content: (
            <ol>
              <li>Keep <code>toggles.reviveEnabled</code> set to true.</li>
              <li>When a tamed cat dies, the collar is delivered to the owner if online.</li>
              <li>If the owner is offline, the collar drops at the cat's death location.</li>
              <li>Use the collar on a block to revive the cat. Only the owner can use it.</li>
            </ol>
          ),
        },
        {
          id: 'soul-transfiguration',
          title: 'Soul Transfiguration (Totem reroll)',
          content: (
            <ul>
              <li>Enable <code>toggles.enableTotemReroll</code> to use Totems on tamed cats.</li>
              <li>This rerolls class, gender, and stats.</li>
              <li>Bond and affinity are reduced by a random 50 to 100 percent.</li>
            </ul>
          ),
        },
        {
          id: 'tune-it',
          title: 'Tune it',
          content: (
            <ul>
              <li><code>toggles.reviveEnabled</code> controls collar creation and revival.</li>
              <li>Notification toggles live under <code>notifs</code>.</li>
              <li>Collars are consumed on revival unless you are in creative mode.</li>
            </ul>
          ),
        },
      ],
    },
    'cats-config': {
      id: 'cats-config',
      title: 'Config Reference',
      group: 'cats',
      version: catsModVersion,
      summary: "Every config option, with friendly notes and defaults.",
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Config', tone: 'info' },
      ],
      quickRef: [
        { label: 'Config file', value: 'config/cozys-improved-cats.json' },
        { label: 'Reload command', value: '/reloadcozycatsconfig' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <p>
              Cozy's Improved Cats uses a simple config file with clear categories. This reference lists every key and
              its default value for Cats Mod v{catsModVersion}.
            </p>
          ),
        },
        {
          id: 'how-to-use',
          title: 'How to use',
          content: (
            <ul>
              <li>Edit <code>config/cozys-improved-cats.json</code> while the game is closed.</li>
              <li>Or use Mod Menu with Cloth Config to edit values in-game.</li>
              <li>Run <code>/reloadcozycatsconfig</code> to reload without restarting.</li>
            </ul>
          ),
        },
        {
          id: 'config-options',
          title: 'Config options',
          content: (
            <>
              <Callout tone="tip" title="Class chance note">
                The <code>genderClassChances</code> section is used for actual class rolls. The <code>classes</code> chance
                values are kept for legacy migration and still appear in the config screen.
              </Callout>
              <ConfigReference
                sections={catsConfigSections}
                filter={configFilter}
                onFilterChange={setConfigFilter}
                inputId="cats-config-filter"
              />
            </>
          ),
        },
        {
          id: 'notes',
          title: 'Notes',
          content: (
            <ul>
              <li>Server config takes priority; align client and server values for consistent behavior.</li>
              <li>Some values, like trade level and drop chance, may require a restart to fully apply.</li>
            </ul>
          ),
        },
      ],
    },
    'cats-compatibility': {
      id: 'cats-compatibility',
      title: 'Compatibility and FAQ',
      group: 'cats',
      version: catsModVersion,
      summary: 'Supported versions, dependencies, and quick answers for everyday players.',
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Support', tone: 'info' },
      ],
      quickRef: [
        { label: 'Loader', value: 'Fabric' },
        { label: 'Needs', value: 'Fabric API (Cloth Config optional)' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <p>
              This page covers supported versions, dependencies, and quick answers for Cozy's Improved Cats.
            </p>
          ),
        },
        {
          id: 'supported-versions',
          title: 'Supported Minecraft versions',
          content: (
            <>
              <p>Cats Mod v{catsModVersion} supports these Minecraft versions:</p>
              <VersionChips versions={catsSupportedVersions} />
            </>
          ),
        },
        {
          id: 'requirements',
          title: 'Requirements',
          content: (
            <ul>
              <li>Fabric Loader + Fabric API are required.</li>
              <li>Cloth Config is optional, but needed for the Mod Menu settings screen.</li>
              <li>Keep client and server configs aligned for consistent behavior.</li>
            </ul>
          ),
        },
        {
          id: 'faq',
          title: 'Quick answers',
          content: (
            <ul>
              <li>Regular cod and salmon only tame cats if <code>toggles.allowVanillaTaming</code> is enabled.</li>
              <li>Abilities do not fire when cats are sitting or too far away.</li>
              <li>Villager trades may require a restart to refresh.</li>
            </ul>
          ),
        },
      ],
    },
    'cats-changelog': {
      id: 'cats-changelog',
      title: 'Changelog',
      group: 'cats',
      version: catsModVersion,
      summary: "Full Cozy's Improved Cats changelog in one scroll.",
      tags: [
        { label: 'Cats', tone: 'accent' },
        { label: 'Changelog', tone: 'info' },
      ],
      quickRef: [
        { label: 'Latest', value: `v${catsModVersion}` },
        { label: 'Includes', value: '1.0.0 -> 1.3.0' },
      ],
      sections: [
        {
          id: 'changelog',
          title: 'Changelog',
          content: (
            <div className="changelog">
              <div className="changelog-entry">
                <h3>1.3.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>
                      <strong>New config toggles</strong>
                      <ul>
                        <li>Mute or disable teleport sound.</li>
                        <li>Disable the gender system (and related effects).</li>
                        <li>Disable revival mechanics (cats no longer drop collars).</li>
                        <li>Enable or disable fall damage.</li>
                        <li>Enable or disable extended debug logging.</li>
                        <li>Allow normal taming (removes Ancient Fish requirement).</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Cloth Config integration</strong> with an in-game config menu and improved docs.
                    </li>
                    <li>
                      <strong>Cat reset / respec option</strong> with a Totem of Undying to reroll attributes and gender.
                    </li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Updated all item textures using the new community-created set.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Fixed</h4>
                  <ul>
                    <li>Achievements pass to ensure triggers and criteria work correctly.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Compatibility</h4>
                  <ul>
                    <li>Safe adoption of existing vanilla tamed cats in existing worlds.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Performance</h4>
                  <ul>
                    <li>Optimization plus a broad bugfix and cleanup sweep.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.2.1</h3>
                <div className="changelog-block">
                  <h4>Fixed</h4>
                  <ul>
                    <li>
                      Fixed a crash in desert biomes where improved cats could spawn without default speed or attack
                      stats.
                    </li>
                    <li>
                      Cats now correctly receive default attributes on spawn.
                    </li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.2.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>Stat system with randomized rolls for tamed cats.</li>
                    <li>Class system: Naiad, Guardian, Witchling, Dreamer, Lucky.</li>
                    <li>Gender system that affects class rolls, breeding behavior, and name color styling.</li>
                    <li>Ability system with class-based triggers and affinity progression.</li>
                    <li>Bond system that grows by caring for cats and keeping them nearby.</li>
                    <li>Trackers for deaths, ability usage, and favorite food.</li>
                    <li>Training String item to view cat stats.</li>
                    <li>20+ advancements.</li>
                    <li>Full config file with most features customizable.</li>
                    <li>QoL: heart bar while feeding, cats follow owner holding raw meat or fish, extra behavior polish.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Visual and audio</h4>
                  <ul>
                    <li>Soul particles and teleport sound when cats teleport.</li>
                    <li>Named cats always render their names.</li>
                    <li>Names are gender color-coded (non-bold).</li>
                    <li>Extra polish and immersion tweaks.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Config system</h4>
                  <ul>
                    <li>JSON config with auto default generation.</li>
                    <li>Live reload command: <code>/reloadcozycatsconfig</code>.</li>
                    <li>Config version with migration support.</li>
                    <li>Categories: trading, catStats, behavior, misc.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Fixed</h4>
                  <ul>
                    <li>Many bug fixes and stability improvements.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.1.1</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>All cat variants now available via spawn eggs.</li>
                    <li>Cats can be tempted with Ancient Cod and Ancient Salmon.</li>
                    <li>Cat collars can now be dyed.</li>
                    <li>Added a small creative mode easter egg.</li>
                    <li>Added more interaction types.</li>
                    <li>Tamed cats can now interact with tamed wolves.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Cats no longer take fall damage.</li>
                    <li>Gift frequency reduced to once per day/night cycle.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Fixed</h4>
                  <ul>
                    <li>If a cat dies while the owner is offline, the collar drops at the cat's last known position.</li>
                    <li>Respawned cats now keep the correct variant and collar color.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.1.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>Cats now hiss when a creeper is within 7 blocks.</li>
                    <li>Cats now purreow when dropping gifts.</li>
                    <li>Added a revive confirmation message.</li>
                    <li>Added advancements for getting Ancient Cod and Ancient Salmon the first time.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Removed modded Creeps and restored vanilla Creepers.</li>
                    <li>Cat-to-cat interactions: 50% chance to interact when nearby.</li>
                    <li>Interaction range: 3 blocks if both sitting, 7 blocks if either is standing.</li>
                    <li>Cat Collar moved to the Tools tab.</li>
                    <li>Ancient fish can now appear as trades from max-level Fisherman villagers.</li>
                    <li>Fishing balance: Ancient Cod/Salmon catch chance reduced to 10% (from 100%).</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Items</h4>
                  <ul>
                    <li>Ancient Cod and Ancient Salmon give a short Regeneration effect when eaten.</li>
                    <li>Same hunger and saturation as raw cod and salmon.</li>
                    <li>Cooked Ancient Cod/Salmon give a longer Regeneration effect when eaten.</li>
                    <li>Same hunger and saturation as cooked cod and salmon.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>UI / tooltips</h4>
                  <ul>
                    <li>Cat Collar tooltip shows the owner's name if the owner was in-game when the cat died.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.0.1</h3>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Updated textures for Cat Collar, Ancient Cod, and Ancient Salmon.</li>
                    <li>Updated Ancient Cod/Salmon effects.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Fixed</h4>
                  <ul>
                    <li>Fixed an issue where previously-tamed vanilla cats could lose owner data in existing worlds.</li>
                    <li>Fixed breeding status appearing for non-owners.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.0.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>
                      <strong>New cat entity</strong> that replaces all vanilla cats with a custom cat type.
                    </li>
                    <li>
                      <strong>Ancient Fish</strong>
                      <ul>
                        <li>Added Ancient Cod and Ancient Salmon as rare drops from cod and salmon.</li>
                        <li>Can also be found as a rare fishing catch.</li>
                        <li>Taming now requires Ancient Cod or Ancient Salmon.</li>
                        <li>Using Ancient Cod/Salmon on tamed cats triggers a surprise effect.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Collar and revive system</strong>
                      <ul>
                        <li>Collar returns to the owner when a tamed cat dies.</li>
                        <li>Added to the owner's inventory or dropped near the owner if full.</li>
                        <li>Right-click the collar to respawn the linked cat.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>New interactions</strong>
                      <ul>
                        <li>Sneak + right click your tamed cat to give it attention.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Gift behavior</strong>
                      <ul>
                        <li>Cats set to follow can find gifts and drop them at their owner's feet.</li>
                      </ul>
                    </li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Cat names are always visible and show in gold when custom-named.</li>
                    <li>Cats have increased health, movement speed, and attack damage.</li>
                    <li>Friendly fire disabled for tamed cats.</li>
                    <li>Creepers flee from cats at a larger radius.</li>
                    <li>Tamed cats periodically send status updates.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Ownership and multiplayer</h4>
                  <ul>
                    <li>Other players cannot breed your tamed cats.</li>
                    <li>Other players cannot use your cat collars.</li>
                    <li>Owners are notified when other players interact with their cat.</li>
                  </ul>
                </div>
              </div>
            </div>
          ),
        },
      ],
    },
    'wolves-getting-started': {
      id: 'wolves-getting-started',
      title: 'Getting Started',
      group: 'wolves',
      version: wolvesModVersion,
      summary: 'Quick setup, taming basics, and the core wolf items.',
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Getting started', tone: 'info' },
      ],
      quickRef: [
        { label: 'Wolves Mod', value: `v${wolvesModVersion}` },
        { label: 'Supported MC', value: wolvesSupportedVersions.join(', ') },
        { label: 'Config file', value: 'config/cozys-improved-wolves.json' },
        { label: 'Reload command', value: '/reloadcozywolvesconfig' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Cozy's Improved Wolves upgrades vanilla wolves with stats, classes, and a revival collar system.
                Existing tamed wolves are safely adopted when the mod loads.
              </p>
              <Callout tone="info" title="Safe adoption">
                Your current wolves keep their owner, name, and collar color when they convert.
              </Callout>
            </>
          ),
        },
        {
          id: 'install',
          title: 'Install and launch',
          content: (
            <ol>
              <li>Install Fabric Loader and Fabric API for your Minecraft version.</li>
              <li>Drop the Cozy's Improved Wolves jar in your mods folder.</li>
              <li>Optional but recommended: install Mod Menu + Cloth Config for the in-game settings screen.</li>
              <li>Launch the game and start taming.</li>
            </ol>
          ),
        },
        {
          id: 'taming-basics',
          title: 'Taming basics',
          content: (
            <ul>
              <li>Taming uses Ancient Bones by default.</li>
              <li>Ancient Bones drop from skeletons or show up in Cleric trades.</li>
              <li>Enable vanilla taming in the config if you want regular bones to work.</li>
              <li>Use a Training Stick (sneak + right click) to see stats and trackers.</li>
            </ul>
          ),
        },
        {
          id: 'supported-versions',
          title: 'Supported Minecraft versions',
          content: (
            <>
              <p>Wolves Mod v{wolvesModVersion} supports these Minecraft versions:</p>
              <VersionChips versions={wolvesSupportedVersions} />
            </>
          ),
        },
        {
          id: 'config-basics',
          title: 'Config basics',
          content: (
            <ul>
              <li>Config file: <code>config/cozys-improved-wolves.json</code>.</li>
              <li>Use <code>/reloadcozywolvesconfig</code> to reload without restarting.</li>
              <li>Toggle gender, revive, fall damage, and more in the config.</li>
            </ul>
          ),
        },
        {
          id: 'related-links',
          title: 'Related links',
          content: (
            <ul>
              <li>
                <PageLink pageId="wolves-items">Items and crafting</PageLink>
              </li>
              <li>
                <PageLink pageId="wolves-config">Config reference</PageLink>
              </li>
              <li>
                <PageLink pageId="wolves-changelog">Full changelog</PageLink>
              </li>
            </ul>
          ),
        },
      ],
    },
    'wolves-features': {
      id: 'wolves-features',
      title: 'Features Overview',
      group: 'wolves',
      version: wolvesModVersion,
      summary: 'The big picture: what changes, what you get, and why wolves feel alive.',
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Features', tone: 'info' },
      ],
      quickRef: [
        { label: 'Taming item', value: 'Ancient Bone' },
        { label: 'Classes', value: 'Hunter, Gatherer' },
        { label: 'Revival', value: 'Wolf Collar' },
        { label: 'Gifts', value: 'Digging + hunting' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Improved Wolves gives every tame a class, stats, and a personality. Hunters and Gatherers play
                differently, and gift hunting turns your wolf into a real partner.
              </p>
              <Callout tone="tip" title="Quick stat check">
                Use a Training Stick (sneak + right click) to show stats, kills, and gifts.
              </Callout>
            </>
          ),
        },
        {
          id: 'core-features',
          title: 'Core features',
          content: (
            <ul>
              <li>Class system: Hunter and Gatherer with different stat ranges.</li>
              <li>Stat rolls on tame plus trackers for deaths, kills, and XP.</li>
              <li>Wolf Collar revival (owner only, config toggle).</li>
              <li>Gift system: gatherers dig gifts, hunters deliver gifts from kills.</li>
              <li>Gender system (optional) that influences class rolls and name colors.</li>
            </ul>
          ),
        },
        {
          id: 'polish',
          title: 'Polish and personality',
          content: (
            <ul>
              <li>Panting, emotes, and status messages that react to what your wolf is doing.</li>
              <li>Howls at night, teleport sounds, and soul particles.</li>
              <li>Named wolves always show their name (gender colors optional).</li>
            </ul>
          ),
        },
        {
          id: 'quality-of-life',
          title: 'Quality-of-life upgrades',
          content: (
            <ul>
              <li>Heart bar while feeding to show health.</li>
              <li>Wolves follow their owner holding meat.</li>
              <li>Owner-only breeding with wild pups by default.</li>
              <li>Safe adoption of existing vanilla tamed wolves.</li>
            </ul>
          ),
        },
        {
          id: 'config-highlights',
          title: 'Config highlights',
          content: (
            <ul>
              <li>Mute teleport sounds, toggle gender, or disable revives.</li>
              <li>Enable fall damage or allow vanilla bone taming.</li>
              <li>Use Totem respec to reroll class and stats.</li>
              <li>See <PageLink pageId="wolves-config">Config reference</PageLink> for all options.</li>
            </ul>
          ),
        },
      ],
    },
    'wolves-classes': {
      id: 'wolves-classes',
      title: 'Wolf Classes',
      group: 'wolves',
      version: wolvesModVersion,
      summary: 'Hunter vs Gatherer, and how class rolls work.',
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Classes', tone: 'info' },
      ],
      quickRef: [
        { label: 'Classes', value: 'Hunter, Gatherer' },
        { label: 'Gender system', value: 'Optional' },
        { label: 'Stat check', value: 'Training Stick' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <p>
              Every tamed wolf rolls a class. If the gender system is enabled, gender nudges the class chances. Use
              a Training Stick to see the full stat roll.
            </p>
          ),
        },
        {
          id: 'hunter',
          title: 'Hunter class',
          content: (
            <ul>
              <li>Higher attack and health rolls.</li>
              <li>Gifts come from hunting kills.</li>
              <li>Best for combat or adventurous play.</li>
            </ul>
          ),
        },
        {
          id: 'gatherer',
          title: 'Gatherer class',
          content: (
            <ul>
              <li>Higher speed and wander rolls.</li>
              <li>Digs up gifts for the owner when nearby.</li>
              <li>Great for base life and utility.</li>
            </ul>
          ),
        },
        {
          id: 'tips',
          title: 'Tips',
          content: (
            <ul>
              <li>Gatherers shine when you hang out near your base or farms.</li>
              <li>Hunters are ideal for exploration and mob-heavy areas.</li>
              <li>Adjust class roll chances in the config if you want more of one class.</li>
            </ul>
          ),
        },
      ],
    },
    'wolves-items': {
      id: 'wolves-items',
      title: 'Items and Crafting',
      group: 'wolves',
      version: wolvesModVersion,
      summary: 'Ancient Bones, collars, and the Training Stick.',
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Items', tone: 'info' },
      ],
      quickRef: [
        { label: 'Taming', value: 'Ancient Bone' },
        { label: 'Training Stick', value: 'Sneak + right click to inspect' },
        { label: 'Wolf Collar', value: 'Revive on block (owner only)' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Cozy's Improved Wolves adds Ancient Bones plus two utility items. Ancient Bones tame wolves and craft
                Ancient Bone Meal, while the Training Stick inspects stats and the Wolf Collar revives fallen wolves.
              </p>
              <Callout tone="tip" title="Item glint">
                Ancient Bones, Bone Meal, Training Sticks, and Wolf Collars always render with a glint so they are easy to spot.
              </Callout>
            </>
          ),
        },
        {
          id: 'item-gallery',
          title: 'Item gallery',
          content: (
            <div className="item-grid">
              <ItemCard
                icon="ancient_bone"
                title="Ancient Bone"
                lines={[
                  'Rare drop from skeletons (chance configurable).',
                  'Required for taming by default.',
                  'Use on tamed wolves for a surprise effect.',
                  'Cleric trades can sell them.',
                ]}
              />
              <ItemCard
                icon="ancient_bone_meal"
                title="Ancient Bone Meal"
                lines={[
                  'Crafted from Ancient Bones.',
                  '4x the fertilization strength of normal bone meal.',
                  'Extra particles on successful use.',
                ]}
              />
              <ItemCard
                icon="training_stick"
                title="Training Stick"
                lines={[
                  'Sneak + right click to view stats and trackers.',
                  'Durability is configurable.',
                  'Crafted from Ancient Bone Meal + Stick.',
                ]}
              />
              <ItemCard
                icon="wolf_collar"
                title="Wolf Collar"
                lines={[
                  'Drops when a tamed wolf dies (if revive enabled).',
                  'Owner-only: right click to respawn.',
                  'Stores the wolf data for revival.',
                ]}
              />
            </div>
          ),
        },
        {
          id: 'getting-items',
          title: 'Getting the items',
          content: (
            <ul>
              <li>Ancient Bones drop from skeletons or are sold by Cleric villagers.</li>
              <li>Ancient Bone Meal is crafted from Ancient Bones.</li>
              <li>Training Stick uses Ancient Bone Meal + a stick.</li>
              <li>Wolf Collars are created when a tamed wolf dies and revive is enabled.</li>
            </ul>
          ),
        },
        {
          id: 'tune-it',
          title: 'Tune it',
          content: (
            <ul>
              <li>Adjust Ancient Bone drop chance in <code>misc.ancientBoneSkeletonDropChance</code>.</li>
              <li>Set Training Stick durability with <code>misc.trainingStickDurability</code>.</li>
              <li>Toggle collar revival with <code>misc.enableReviveMechanics</code>.</li>
              <li>Allow vanilla bone taming with <code>misc.allowVanillaTaming</code>.</li>
            </ul>
          ),
        },
      ],
    },
    'wolves-interactions': {
      id: 'wolves-interactions',
      title: 'Interactions and Emotes',
      group: 'wolves',
      version: wolvesModVersion,
      summary: 'Petting, emotes, and the messages wolves send.',
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Interactions', tone: 'info' },
      ],
      quickRef: [
        { label: 'Petting', value: 'Sneak + right click' },
        { label: 'Emotes', value: 'Solo + paired' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                Interactions are all about bonding. Emotes add personality based on time, and weather.
              </p>
            </>
          ),
        },
        {
          id: 'petting',
          title: 'Petting your wolf',
          content: (
            <ul>
              <li>Sneak + right click to give attention.</li>
              <li>Interactions complete after a few quick taps.</li>
            </ul>
          ),
        },
        {
          id: 'emotes',
          title: 'Emotes and messages',
          content: (
            <ul>
              <li>Wolves pant when given attention.</li>
              <li>Status messages describe what your wolf is up to.</li>
              <li>Nighttime howls add extra flavor.</li>
              <li>Paired emotes trigger when two tamed wolves are nearby.</li>
            </ul>
          ),
        },
        {
          id: 'feeding',
          title: 'Feeding feedback',
          content: (
            <ul>
              <li>Heart bar shows health while feeding.</li>
              <li>Owners get alerts when someone else feeds or pets their wolf.</li>
              <li>Kill and gift notifications can be toggled in config.</li>
            </ul>
          ),
        },
        {
          id: 'tune-it',
          title: 'Tune it',
          content: (
            <ul>
              <li>Adjust petting timing with <code>misc.secondsToInteract</code> and <code>misc.interactionsToCompletion</code>.</li>
              <li>Toggle solo or paired emotes in <code>behavior.enableSoloEmotes</code> and <code>behavior.enablePairedEmotes</code>.</li>
              <li>Turn specific notifications on or off in the <code>notifs</code> section.</li>
            </ul>
          ),
        },
      ],
    },
    'wolves-revival': {
      id: 'wolves-revival',
      title: 'Revival and Soul Transfiguration',
      group: 'wolves',
      version: wolvesModVersion,
      summary: 'Bring wolves back with a Wolf Collar, or reroll with a Totem if you want a fresh start.',
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Revival', tone: 'info' },
      ],
      quickRef: [
        { label: 'Wolf Collar Drop', value: 'On tamed wolf death' },
        { label: 'Revive', value: 'Use collar on a block' },
        { label: 'Totem Reroll', value: 'Optional config toggle' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <>
              <p>
                When a tamed wolf dies, the mod creates a Wolf Collar containing its data. Using that collar on a block
                revives the same wolf with its stored stats and identity.
              </p>
              <p>
                A separate feature called Soul Transfiguration lets you use a Totem of Undying on a tamed wolf to reroll its class, gender, and stats.
              </p>
            </>
          ),
        },
        {
          id: 'revival-flow',
          title: 'Revival flow',
          content: (
            <ol>
              <li>Keep <code>misc.enableReviveMechanics</code> set to true.</li>
              <li>When a tamed wolf dies, the collar is delivered to the owner if possible.</li>
              <li>If the owner's inventory is full, the collar drops nearby.</li>
              <li>Use the collar on a block to revive the wolf. Only the owner can use it.</li>
            </ol>
          ),
        },
        {
          id: 'respec',
          title: 'Respec option (Totem reroll)',
          content: (
            <ul>
              <li>Enable <code>behavior.enableTotemReroll</code> in the config to use Totems on tamed wolves.</li>
              <li>This rerolls class, gender, and stats.</li>
            </ul>
          ),
        },
        {
          id: 'tune-it',
          title: 'Tune it',
          content: (
            <ul>
              <li>Toggle collar revives with <code>misc.enableReviveMechanics</code>.</li>
              <li>Enable respec with <code>behavior.enableTotemReroll</code>.</li>
              <li>Allow fall damage with <code>behavior.allowFallDamage</code>.</li>
            </ul>
          ),
        },
      ],
    },
    'wolves-config': {
      id: 'wolves-config',
      title: 'Config Reference',
      group: 'wolves',
      version: wolvesModVersion,
      summary: 'Every config option, with friendly notes and defaults.',
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Config', tone: 'info' },
      ],
      quickRef: [
        { label: 'Config file', value: 'config/cozys-improved-wolves.json' },
        { label: 'Reload command', value: '/reloadcozywolvesconfig' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <p>
              Cozy's Improved Wolves uses a simple config file with clear categories. This reference lists every key and its default value for Wolves Mod v1.4.0.
            </p>
          ),
        },
        {
          id: 'how-to-use',
          title: 'How to use',
          content: (
            <ul>
              <li>Edit <code>config/cozys-improved-wolves.json</code> while the game is closed.</li>
              <li>Or use Mod Menu with Cloth Config to edit values in-game.</li>
              <li>Run <code>/reloadcozywolvesconfig</code> to reload without restarting.</li>
            </ul>
          ),
        },
        {
          id: 'config-options',
          title: 'Config options',
          content: (
            <>
              <Callout tone="tip" title="Class chance note">
                Gender-based class chances live in <code>misc.maleChanceForHunter</code> and <code>misc.femaleChanceForGatherer</code>.
              </Callout>
              <ConfigReference
                sections={wolvesConfigSections}
                filter={wolvesConfigFilter}
                onFilterChange={setWolvesConfigFilter}
                inputId="wolves-config-filter"
              />
            </>
          ),
        },
        {
          id: 'notes',
          title: 'Notes',
          content: (
            <ul>
              <li>Server config takes priority; align client and server values for consistent behavior.</li>
              <li>Some values, like trade level and drop chance, may require a restart to fully apply.</li>
            </ul>
          ),
        },
      ],
    },
    'wolves-compatibility': {
      id: 'wolves-compatibility',
      title: 'Compatibility and FAQ',
      group: 'wolves',
      version: wolvesModVersion,
      summary: 'Supported versions, dependencies, and quick answers for everyday players.',
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Support', tone: 'info' },
      ],
      quickRef: [
        { label: 'Loader', value: 'Fabric' },
        { label: 'Needs', value: 'Fabric API (Cloth Config optional)' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <p>
              This page covers supported versions, dependencies, and quick answers for Cozy's Improved Wolves.
            </p>
          ),
        },
        {
          id: 'supported-versions',
          title: 'Supported Minecraft versions',
          content: (
            <>
              <p>Wolves Mod v{wolvesModVersion} supports these Minecraft versions:</p>
              <VersionChips versions={wolvesSupportedVersions} />
            </>
          ),
        },
        {
          id: 'requirements',
          title: 'Requirements',
          content: (
            <ul>
              <li>Fabric Loader and Fabric API are required.</li>
              <li>Cloth Config + Mod Menu are optional but make settings easier.</li>
              <li>Server and client should use matching mod versions.</li>
            </ul>
          ),
        },
        {
          id: 'faq',
          title: 'Quick Answers',
          content: (
            <ul>
              <li>Regular bones only tame wolves if vanilla taming is enabled.</li>
              <li>Villager trades may require a restart to refresh.</li>
            </ul>
          ),
        },
      ],
    },
    'wolves-changelog': {
      id: 'wolves-changelog',
      title: 'Changelog',
      group: 'wolves',
      version: wolvesModVersion,
      summary: "Full Cozy's Improved Wolves changelog in one scroll.",
      tags: [
        { label: 'Wolves', tone: 'accent' },
        { label: 'Release notes', tone: 'info' },
      ],
      quickRef: [
        { label: 'Latest', value: `v${wolvesModVersion}` },
        { label: 'Includes', value: '1.0.0 -> 1.4.0' },
      ],
      sections: [
        {
          id: 'changelog',
          title: 'Changelog',
          content: (
            <div className="changelog">
              <div className="changelog-entry">
                <h3>1.4.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>New config toggles for teleport sound, gender system, revive mechanics, fall damage, and logging.</li>
                    <li>Allow normal taming to remove the Ancient Bone requirement.</li>
                    <li>Cloth Config integration with an in-game config menu.</li>
                    <li>Optional respec: use a Totem of Undying to reroll attributes and gender.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Updated all item textures with the new community-created set.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Fixed</h4>
                  <ul>
                    <li>Wolf armor now applies correctly.</li>
                    <li>Achievement triggers and criteria now work as intended.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Compatibility</h4>
                  <ul>
                    <li>Safe adoption of existing vanilla tamed wolves in older worlds.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Performance</h4>
                  <ul>
                    <li>Optimization and bugfix sweep across the mod.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.3.1</h3>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Improved statistics management for better tracking.</li>
                    <li>General optimizations and internal cleanup.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Config</h4>
                  <ul>
                    <li>Better config restoration to recover and preserve settings.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.3.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>Stat system with unique randomized rolls per wolf.</li>
                    <li>Class system: Hunter and Gatherer.</li>
                    <li>Gender system affecting class rolls, breeding, and name colors.</li>
                    <li>Training Stick item to view stats.</li>
                    <li>Gift system with class-based gift behavior.</li>
                    <li>Trackers for deaths, kills, and experience.</li>
                    <li>20+ new advancements.</li>
                    <li>Full config file with most features customizable.</li>
                    <li>Heart bar while feeding and food-based follow behavior.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Visual and audio</h4>
                  <ul>
                    <li>Soul particles and teleport sound when wolves teleport.</li>
                    <li>Named wolves always render their names.</li>
                    <li>Names color-coded by gender.</li>
                    <li>Growth sparkles for Ancient Bone Meal.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Config system</h4>
                  <ul>
                    <li>JSON config with default generation.</li>
                    <li>Live reload command: <code>/reloadcozywolvesconfig</code>.</li>
                    <li>Config version tracking with graceful migration.</li>
                    <li>Categories: trading, wolfStats, behavior, misc.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Fixed</h4>
                  <ul>
                    <li>Many bug fixes and stability improvements.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.2.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>Gift digging that places items directly into the owner inventory.</li>
                    <li>Ancient Bone Meal with 4x fertilization strength.</li>
                    <li>Advancements for obtaining Ancient Bone and Ancient Bone Meal.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Taming chance increased to 50%.</li>
                    <li>Action messages now only appear for the owner.</li>
                    <li>Wolf-to-wolf interactions trigger within 3 blocks sitting or 7 blocks standing.</li>
                    <li>Increased message range for action updates.</li>
                    <li>Removed custom textures for compatibility.</li>
                    <li>Wolves follow more closely and wander less while exploring.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.1.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>Feeding feedback for full wolves and breeding status.</li>
                    <li>Owner alerts for feeding, Ancient Bone use, interactions, and kills.</li>
                    <li>New interaction behaviors with panting and status messages.</li>
                    <li>Nighttime howl chance.</li>
                    <li>Revival confirmation message.</li>
                    <li>Audio feedback when giving a tamed wolf an Ancient Bone.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Undying Bone renamed to Ancient Bone with a new texture.</li>
                    <li>Custom names now render in gold.</li>
                    <li>Other players must interact 3 times to complete an action.</li>
                    <li>Breeding two tamed wolves produces a baby wild wolf.</li>
                    <li>Taming chance reduced to 50%.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Fixed / compatibility</h4>
                  <ul>
                    <li>Existing tamed wolves remain tamed when the mod is added.</li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Ownership and multiplayer</h4>
                  <ul>
                    <li>Prevented players from reviving wolves they do not own.</li>
                    <li>Wolf Collar tooltips show the owner when that player is in-game.</li>
                  </ul>
                </div>
              </div>

              <div className="changelog-entry">
                <h3>1.0.0</h3>
                <div className="changelog-block">
                  <h4>Added</h4>
                  <ul>
                    <li>
                      <strong>New wolf entity</strong>
                      <ul>
                        <li>All vanilla wolves are replaced with a custom wolf type.</li>
                        <li>Updated wolf texture.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Undying Bone</strong>
                      <ul>
                        <li>Added Undying Bone as a rare drop from skeletons.</li>
                        <li>Taming now requires an Undying Bone.</li>
                        <li>Undying Bones can be used on tamed wolves for a surprise effect.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Collar and revive system</strong>
                      <ul>
                        <li>Collar returns to the owner when a tamed wolf dies.</li>
                        <li>Added to the owner's inventory or dropped near the owner if full.</li>
                        <li>Right click the collar to respawn the linked wolf.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>New interaction</strong>
                      <ul>
                        <li>Sneak + right click your tamed wolf to interact with it.</li>
                      </ul>
                    </li>
                  </ul>
                </div>
                <div className="changelog-block">
                  <h4>Changed</h4>
                  <ul>
                    <li>Wolf names are always visible when custom-named.</li>
                    <li>Tamed wolves have increased health, movement speed, and attack damage.</li>
                    <li>Friendly fire disabled for tamed wolves.</li>
                  </ul>
                </div>
              </div>
            </div>
          ),
        },
      ],
    },
    'shared-installation': {
      id: 'shared-installation',
      title: 'Installation',
      group: 'shared',
      summary: 'Quick setup steps for Cozy mods on Fabric.',
      tags: [
        { label: 'Shared', tone: 'accent' },
        { label: 'Setup', tone: 'info' },
      ],
      quickRef: [
        { label: 'Loader', value: 'Fabric' },
        { label: 'Config UI', value: 'Mod Menu + Cloth Config' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: (
            <p>
              A simple setup checklist for Cozy mods on Fabric.
            </p>
          ),
        },
        {
          id: 'install-steps',
          title: 'Install steps',
          content: (
            <ol>
              <li>Install Fabric Loader for the target Minecraft version.</li>
              <li>Add Fabric API to the mods folder.</li>
              <li>Drop Cozy mod jars into the mods folder.</li>
              <li>Install Mod Menu and Cloth Config for in-game settings.</li>
            </ol>
          ),
        },
        {
          id: 'tips',
          title: 'Tips',
          content: (
            <ul>
              <li>Match Fabric API to your Minecraft version.</li>
              <li>Keep client and server mod lists aligned in multiplayer.</li>
            </ul>
          ),
        },
        {
          id: 'related-links',
          title: 'Related links',
          content: (
            <ul>
              <li>
                <PageLink pageId="cats-getting-started">Cats getting started</PageLink>
              </li>
              <li>
                <PageLink pageId="wolves-getting-started">Wolves getting started</PageLink>
              </li>
            </ul>
          ),
        },
      ],
    },
    'shared-troubleshooting': {
      id: 'shared-troubleshooting',
      title: 'Troubleshooting',
      group: 'shared',
      summary: 'Quick fixes for common Cozy mod hiccups.',
      tags: [
        { label: 'Shared', tone: 'accent' },
        { label: 'Support', tone: 'info' },
      ],
      quickRef: [
        { label: 'Reload commands', value: '/reloadcozycatsconfig, /reloadcozywolvesconfig' },
        { label: 'Config location', value: 'config/*.json' },
      ],
      sections: [
        {
          id: 'what-it-is',
          title: 'What it is',
          content: <p>Quick fixes for common setup and config issues.</p>,
        },
        {
          id: 'common-fixes',
          title: 'Common fixes',
          content: (
            <ul>
              <li>Confirm Fabric API is installed and matches your Minecraft version.</li>
              <li>Check that mod jars are not duplicated across loaders.</li>
              <li>Open the log file to confirm the mod loaded correctly.</li>
            </ul>
          ),
        },
        {
          id: 'config-tips',
          title: 'Config tips',
          content: (
            <ul>
              <li>Delete the config file to regenerate defaults.</li>
              <li>Run the reload command after edits.</li>
              <li>Server config overrides client config in multiplayer.</li>
            </ul>
          ),
        },
        {
          id: 'notes',
          title: 'Notes',
          content: (
            <ul>
              <li>Villager trades may require a restart to update.</li>
              <li>Future mods and version updates planned.</li>
            </ul>
          ),
        },
      ],
    },
  };

  const navGroups: NavGroup[] = [
    { id: 'home', label: 'Home', pages: ['home'] },
    {
      id: 'cats',
      label: (
        <span className="nav-group-label">
          <img src="/assets/items/cat_spawn_egg.png" alt="Cat spawn egg" className="nav-icon" />
          Improved Cats
        </span>
      ),
      pages: [
        'cats-getting-started',
        'cats-features',
        'cats-classes',
        'cats-items',
        'cats-interactions',
        'cats-revival',
        'cats-config',
        'cats-compatibility',
        'cats-changelog',
      ],
    },
    {
      id: 'wolves',
      label: (
        <span className="nav-group-label">
          <img src="/assets/items/wolf_spawn_egg.png" alt="Wolf spawn egg" className="nav-icon" />
          Improved Wolves
        </span>
      ),
      pages: [
        'wolves-getting-started',
        'wolves-features',
        'wolves-classes',
        'wolves-items',
        'wolves-interactions',
        'wolves-revival',
        'wolves-config',
        'wolves-compatibility',
        'wolves-changelog',
      ],
    },
    {
      id: 'shared',
      label: 'Shared',
      pages: ['shared-installation', 'shared-troubleshooting'],
    },
  ];

  const groupHomes: Record<string, string> = {
    home: 'home',
    cats: 'cats-getting-started',
    wolves: 'wolves-getting-started',
    shared: 'shared-installation',
  };

  const groupLabels: Record<string, string> = {
    home: 'Home',
    cats: 'Improved Cats',
    wolves: 'Improved Wolves',
    shared: 'Shared',
  };

  const currentPage = pages[currentPageId] || pages.home;
  const flatPageOrder = navGroups.flatMap((group) => group.pages);
  const currentIndex = flatPageOrder.indexOf(currentPage.id);
  const prevPageId = currentIndex > 0 ? flatPageOrder[currentIndex - 1] : null;
  const nextPageId = currentIndex >= 0 && currentIndex < flatPageOrder.length - 1 ? flatPageOrder[currentIndex + 1] : null;

  const normalizedSearch = navSearch.trim().toLowerCase();
  const filteredNavGroups = navGroups
    .map((group) => {
      if (!normalizedSearch) return group;
      const pagesFiltered = group.pages.filter((pageId) =>
        pages[pageId]?.title.toLowerCase().includes(normalizedSearch)
      );
      return { ...group, pages: pagesFiltered };
    })
    .filter((group) => group.pages.length > 0);

  const breadcrumbs = [
    { label: 'Home', pageId: 'home' },
    ...(currentPage.group !== 'home'
      ? [{ label: groupLabels[currentPage.group] || currentPage.group, pageId: groupHomes[currentPage.group] }]
      : []),
    { label: currentPage.title, pageId: currentPage.id },
  ];

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="site-header">
        <div className="brand">
          <img className="logo-mark" src="/assets/items/grass_block_side.png" alt="Minecraft grass block" />
          <div>
            <div className="brand-title">Cozy Mods Wiki</div>
            <div className="brand-subtitle">Retro field guide for improved companions</div>
          </div>
        </div>
        <div className="header-actions">
          <button type="button" className="nav-toggle" onClick={() => setNavOpen(true)}>
            Open Nav
          </button>
          <button
            type="button"
            className="theme-toggle"
            onClick={() => setDarkMode((prev) => !prev)}
            aria-pressed={darkMode}
          >
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
          <ExternalLink href="https://modrinth.com/mod/cozys-improved-cats">Cats Modrinth</ExternalLink>
          <ExternalLink href="https://www.curseforge.com/minecraft/mc-mods/cozys-improved-cats">Cats CurseForge</ExternalLink>
          <ExternalLink href="https://modrinth.com/mod/cozys-improved-wolves">Wolves Modrinth</ExternalLink>
          <ExternalLink href="https://www.curseforge.com/minecraft/mc-mods/cozys-improved-wolves">
            Wolves CurseForge
          </ExternalLink>
          <ExternalLink href="https://discord.gg/9u4dnSpyjJ">Discord</ExternalLink>
        </div>
      </header>

      <div className="layout">
        <aside className={`sidebar ${navOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h2>Navigation</h2>
            <button type="button" className="sidebar-close" onClick={() => setNavOpen(false)}>
              Close
            </button>
          </div>
          <div className="search-field">
            <label htmlFor="nav-search">Search pages</label>
            <input
              id="nav-search"
              type="search"
              placeholder="Filter by title"
              value={navSearch}
              onChange={(event) => setNavSearch(event.target.value)}
            />
          </div>
          <nav>
            {filteredNavGroups.map((group) => (
              <div className="nav-group" key={group.id}>
                <div className="nav-group-title">{group.label}</div>
                <div className="nav-group-links">
                  {group.pages.map((pageId) => (
                    <button
                      key={pageId}
                      type="button"
                      className={`nav-link ${currentPage.id === pageId ? 'active' : ''}`}
                      onClick={() => handleSelectPage(pageId)}
                      aria-current={currentPage.id === pageId ? 'page' : undefined}
                    >
                      {pages[pageId]?.title}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {navOpen && <button type="button" className="nav-overlay" onClick={() => setNavOpen(false)} />}

        <main id="main-content" className="page">
          <div className="breadcrumbs">
            {breadcrumbs.map((crumb, index) => (
              <span key={`${crumb.label}-${index}`}>
                {index > 0 && <span className="breadcrumb-sep">/</span>}
                {crumb.pageId === currentPage.id ? (
                  <span>{crumb.label}</span>
                ) : (
                  <button type="button" className="breadcrumb-link" onClick={() => handleSelectPage(crumb.pageId)}>
                    {crumb.label}
                  </button>
                )}
              </span>
            ))}
          </div>

          <div className="page-header">
            <div className="page-title-row">
              <h1 className="page-title">{currentPage.title}</h1>
              {currentPage.version && <span className="version-badge">Applies to {currentPage.version}</span>}
            </div>
            <p className="page-summary">{currentPage.summary}</p>
            {currentPage.tags && (
              <div className="tag-row">
                {currentPage.tags.map((tag) => (
                  <span key={tag.label} className={`tag tag-${tag.tone}`}>
                    {tag.label}
                  </span>
                ))}
              </div>
            )}
            {currentPage.quickRef && (
              <div className="quick-ref">
                {currentPage.quickRef.map((item) => (
                  <div key={item.label} className="quick-ref-item">
                    <span className="quick-ref-label">{item.label}</span>
                    <span className="quick-ref-value">{item.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <details className="toc-mobile">
            <summary>On this page</summary>
            <div className="toc-links">
              {currentPage.sections.map((section) => (
                <a key={section.id} href={`#${section.id}`}>
                  {section.title}
                </a>
              ))}
            </div>
          </details>

          {currentPage.sections.map((section) => (
            <section key={section.id} id={section.id} className="page-section">
              <h2>{section.title}</h2>
              {section.content}
            </section>
          ))}

          <div className="pager">
            {prevPageId && (
              <button type="button" className="pager-link" onClick={() => handleSelectPage(prevPageId)}>
                Prev: {pages[prevPageId]?.title}
              </button>
            )}
            {nextPageId && (
              <button type="button" className="pager-link" onClick={() => handleSelectPage(nextPageId)}>
                Next: {pages[nextPageId]?.title}
              </button>
            )}
          </div>
        </main>

        <aside className="toc">
          <div className="toc-card">
            <div className="toc-title">On this page</div>
            <div className="toc-links">
              {currentPage.sections.map((section) => (
                <a key={section.id} href={`#${section.id}`}>
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <footer className="site-footer">
        <div>2026 Cozy's Improved Mods. Crafted for pixel adventurers.</div>
      </footer>
    </div>
  );
};

export default CozyModsWiki;
